/**
 * EditAccountModal Component
 * 
 * Modal for editing existing accounts.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Pre-filled account form
 * - Mark as Sold for assets / close account for others
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef, useState } from 'react';
import { AccountForm } from './AccountForm';
import { Account, AccountType, CloseAccountRequest, UpdateAccountPayload } from '../../types/account.types';
import { DeleteFinancialAccountModal } from './DeleteFinancialAccountModal';
import { MarkAssetAsSoldModal, MarkAssetAsSoldPayload } from './MarkAssetAsSoldModal';
import { X, AlertTriangle } from 'lucide-react';

interface EditAccountModalProps {
  account: Account;
  onClose: () => void;
  onSubmit: (data: UpdateAccountPayload) => void;
  onDelete?: () => void;
  onMarkAsSold?: (payload: CloseAccountRequest & { notesAppend?: string }) => void;
}

export function EditAccountModal({
  account,
  onClose,
  onSubmit,
  onDelete,
  onMarkAsSold
}: EditAccountModalProps): React.ReactElement {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [soldLoading, setSoldLoading] = useState(false);

  const isAsset = account.type === AccountType.ASSETS;

  // Focus trap and keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showSoldModal) {
          setShowSoldModal(false);
        } else if (showCloseWarning) {
          setShowCloseWarning(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, showCloseWarning, showSoldModal]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (showSoldModal) {
        setShowSoldModal(false);
      } else if (showCloseWarning) {
        setShowCloseWarning(false);
      } else {
        onClose();
      }
    }
  };

  const handleConfirmClose = () => {
    const today = new Date().toISOString().split('T')[0];
    onSubmit({
      isActive: false,
      closureDate: today,
      includeInNetWorth: false,
    });
    setShowCloseWarning(false);
  };

  const handleMarkAsSold = (payload: MarkAssetAsSoldPayload) => {
    setSoldLoading(true);
    try {
      if (onMarkAsSold) {
        onMarkAsSold(payload);
      } else {
        // Fallback: use update path
        const notes = payload.notesAppend
          ? [account.notes, payload.notesAppend].filter(Boolean).join('\n')
          : account.notes;
        onSubmit({
          isActive: false,
          closureDate: payload.closureDate,
          closureReason: payload.closureReason,
          includeInNetWorth: false,
          currentValue: 0,
          notes
        });
      }
      setShowSoldModal(false);
      onClose();
    } finally {
      setSoldLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-account-title"
      aria-describedby="edit-account-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="edit-account-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit Account
              </h2>
              <p id="edit-account-description" className="text-sm text-gray-600 dark:text-gray-400">
                Update account information for {account.name}
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {showCloseWarning ? (
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Close Account
                    </h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      Are you sure you want to close this account? This action will mark the account as closed and exclude it from active account lists.
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCloseWarning(false)}
                    className="btn-secondary btn-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmClose}
                    className="btn-danger btn-md"
                  >
                    Close Account
                  </button>
                </div>
              </div>
            ) : (
              <>
                <AccountForm
                  account={account}
                  onSubmit={onSubmit}
                  onCancel={onClose}
                />

                {/* Mark as Sold / Close (active accounts only) */}
                {account.isActive && (
                  <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                    {isAsset ? (
                      <div className="rounded-lg border border-amber-200 dark:border-amber-800 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-amber-800 dark:text-amber-300">
                              Mark as Sold
                            </div>
                            <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              Close this asset, set value to $0, and exclude it from net worth. Keeps history.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowSoldModal(true)}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm shrink-0"
                          >
                            Mark as Sold
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                              Close Account
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Mark as closed and hide from active account lists.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowCloseWarning(true)}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors text-sm shrink-0"
                          >
                            Close Account
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Danger Zone */}
                {account.isActive && onDelete && (
                  <div className="mt-6 border-t border-red-200 dark:border-red-800 pt-6">
                    <div className="rounded-lg border border-red-200 dark:border-red-800 p-4">
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-red-700 dark:text-red-400">Danger Zone</h4>
                        <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                          Irreversible actions for this account
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-red-700 dark:text-red-400">Delete Account</div>
                          <div className="text-xs text-red-600 dark:text-red-500 mt-1">
                            Permanently delete this account and all data
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowDeleteModal(true)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {onDelete && (
        <DeleteFinancialAccountModal
          isOpen={showDeleteModal}
          accountName={account.name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            onDelete();
            setShowDeleteModal(false);
            onClose();
          }}
        />
      )}

      {isAsset && (
        <MarkAssetAsSoldModal
          account={account}
          isOpen={showSoldModal}
          onClose={() => setShowSoldModal(false)}
          onConfirm={handleMarkAsSold}
          loading={soldLoading}
        />
      )}
    </div>
  );
}
